import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { InfoIcon } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  useFundBasketLinkedSips,
  useRebalanceFundBasketLinkedSips,
} from "creso-core/hooks";
import { formatAsCrore, getInitials } from "creso-core/utils";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  V2Loader,
} from "web-design-system";
import { Avatar } from "web-core";
import { handleError } from "@/utils/error";
import { useNavigate } from "@tanstack/react-router";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { FundBasketFormValues } from "./dialog";
import { LinkedSipEmptyStateIcon } from "./icons/linked-sip-empty-state";

export function BasketRebalanceReview(props: { next: () => void }) {
  const { next } = props;
  const form = useFormContext<FundBasketFormValues>();
  const basketId = form.getValues("id") as number;

  const { data, isLoading } = useFundBasketLinkedSips(basketId, {
    enabled: !!basketId,
  });
  const { mutateAsync: rebalance, isPending } =
    useRebalanceFundBasketLinkedSips(basketId);

  const outdated = useMemo(
    () => data?.filter((sip) => sip.match_state === "OUTDATED") ?? [],
    [data]
  );
  const custom = useMemo(
    () => data?.filter((sip) => sip.match_state === "PARTIAL") ?? [],
    [data]
  );

  const navigate = useNavigate();

  if (isLoading) return null;

  const handleUpdate = async () => {
    try {
      const batchIds = Array.from(new Set(outdated.map((sip) => sip.batch_id)));
      const result = await rebalance({
        batch_ids: batchIds,
        dry_run: false,
      });
      const updatedText = `Updated ${result.changes_needed_count} SIP${result.changes_needed_count === 1 ? "" : "s"}`;
      const blockedText = result.blocked_count
        ? `, ${result.blocked_count} need manual review`
        : "";
      toast.success(`${updatedText}${blockedText}`);
      next();
    } catch (error) {
      handleError(error, "Failed to update SIPs");
    }
  };

  return (
    <V2Loader isLoading={isLoading} className="flex flex-col size-full">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
        <span className="text-lg font-semibold">Review matching SIPs</span>
        <div className="overflow-hidden flex flex-col bg-linear-to-b flex-1 from-tag-yellow/50 to-background rounded-lg border border-border">
          <ConditionalRenderer condition={outdated.length > 0}>
            <div className="flex flex-col gap-1 p-4">
              <span className="text-sm font-semibold">
                {outdated.length} SIP{outdated.length === 1 ? "" : "s"} matched
                the previous basket profile
              </span>
              <span className="text-xs text-muted-foreground">
                You may update them with the basket or review later in the
                linked SIP page.
              </span>
            </div>
            <Table className="w-full flex-1 text-sm">
              <TableHeader>
                <TableRow className="border-none text-left">
                  <TableHead className="py-1 h-auto text-xs text-muted-foreground font-semibold">
                    Client name
                  </TableHead>
                  <TableHead className="py-1 h-auto text-xs text-muted-foreground font-semibold">
                    Start Date
                  </TableHead>
                  <TableHead className="py-1 h-auto text-xs text-muted-foreground text-right font-semibold">
                    SIP amount
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outdated.map((sip) => (
                  <TableRow
                    key={sip.batch_id}
                    className="border-none hover:bg-transparent"
                  >
                    <TableCell className="py-3">
                      <div className="flex flex-row items-center gap-3">
                        <Avatar
                          fallback={getInitials(sip.client_name ?? "")}
                          url=""
                          size="sm"
                        />
                        <span>{sip.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      {sip.start_date
                        ? format(new Date(sip.start_date), "dd MMM yyyy")
                        : "-"}
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      {formatAsCrore(sip.sip_amount, 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!outdated.length}>
            <div className="flex flex-col flex-1 gap-4 items-center justify-center">
              <LinkedSipEmptyStateIcon />
              <span className="text-sm text-muted-foreground">
                No SIPs match the previous basket profile.
              </span>
            </div>
          </ConditionalRenderer>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-border p-5">
        {custom.length > 0 ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <InfoIcon className="size-4 shrink-0" />
            {custom.length} more SIP{custom.length === 1 ? "" : "s"} use a
            custom version this basket
          </div>
        ) : (
          <div />
        )}
        <div className="ml-auto flex items-center gap-3">
          {custom.length > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                navigate({
                  to: "/funds/basket/$basket_id/linked-sip",
                  params: { basket_id: basketId },
                  search: {},
                  replace: true,
                });
                next();
              }}
            >
              Review All
            </Button>
          )}
          {outdated.length > 0 && (
            <Button
              disabled={outdated.length === 0 || isPending}
              onClick={handleUpdate}
            >
              Update Matching SIPs
            </Button>
          )}
          {outdated.length === 0 && custom.length === 0 && (
            <Button onClick={() => next()}>Done</Button>
          )}
        </div>
      </div>
    </V2Loader>
  );
}
